// This is a service that uses the Angular Hono client to make requests to the Dealcore API.
// And this is the only exception for eslint-disable-next-line @nx/enforce-module-boundaries!!!

// ********************************************************************************************
// ********************************************************************************************
import { Injectable } from '@angular/core';
import type { Identifiers } from '@hyundai/shared-lib/edit';
import { DealerFeature } from '../+state';
import type {
    AboutUsTeaser,
    EditTextListPayload,
    ImageTextContent,
    SaveEmployee,
    UpsertDealerCampaignPayload,
} from './api-types';
import { DealerApiService } from './dealer-api.service';

@Injectable({ providedIn: 'root' })
export class EditApiService extends DealerApiService {
    dealerSlug = this.store.selectSignal(DealerFeature.state.selectSubDealerOrDealerOrVehicleMarketDealerSlug);

    async postContentText(identifier: Identifiers, value: string) {
        const request = await this.api[':slug'].edit['text-content'].$post({
            param: {
                slug: this.dealerSlug(),
            },
            json: {
                identifier,
                value,
            },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async postContentBullet(identifier: Identifiers, value: string) {
        const request = await this.api[':slug'].edit['text-content'].$post({
            param: {
                slug: this.dealerSlug(),
            },
            json: {
                identifier,
                value,
            },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async postSetting(identifier: Identifiers, value: string) {
        const request = await this.api[':slug'].edit['setting'].$post({
            param: {
                slug: this.dealerSlug(),
            },
            json: {
                identifier,
                value,
            },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async postImage(identifier: Identifiers, image: string) {
        const request = await this.api[':slug'].edit.image.$post({
            param: { slug: this.dealerSlug() },
            json: {
                identifier,
                image,
            },
        });
        return request.json();
    }

    async postAboutUsTeasers(teasers: AboutUsTeaser[], teasersToRemove: number[]) {
        const request = await this.api[':slug'].edit['about-us-teaser'].$post({
            param: { slug: this.dealerSlug() },
            json: {
                teasers,
                teasersToRemove,
            },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async postTmpImage(identifier: Identifiers, file: File) {
        const request = await this.api.image['tmp-image'].$post({
            form: {
                identifier,
                file,
            },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getEmployees() {
        const request = await this.api[':slug'].edit['list-employees'].$get({
            param: { slug: this.dealerSlug() },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async saveEmployees(employees: SaveEmployee[]) {
        const request = await this.api[':slug'].edit['save-employees'].$post({
            param: { slug: this.dealerSlug() },
            json: employees,
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getDealerCampaigns() {
        const request = await this.api[':slug']['dealer-campaigns'].edit.list.$get({
            param: { slug: this.dealerSlug() },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getTextListBullets(identifier: Identifiers) {
        const request = await this.api[':slug'].edit['get-text-list-bullets'][':identifier'].$get({
            param: { slug: this.dealerSlug(), identifier },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getServiceList() {
        const request = await this.api[':slug'].edit['services'].$get({
            param: { slug: this.dealerSlug() },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getHomeTeaserList() {
        const request = await this.api[':slug'].edit['home-teasers'].$get({
            param: { slug: this.dealerSlug() },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async saveTextList(identifier: Identifiers, value: EditTextListPayload) {
        const request = await this.api[':slug'].edit['save-text-list'].$post({
            param: { slug: this.dealerSlug() },
            json: {
                identifier,
                value,
            },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getDealerCampaignEditDetail(campaignId: number) {
        const request = await this.api[':slug']['dealer-campaigns'].edit.detail[':campaignId'].$get({
            param: { slug: this.dealerSlug(), campaignId: `${campaignId}` },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getModelEnvkvDropdown() {
        const request = await this.api[':slug']['dealer-campaigns'].edit['model-envkv-dropdown'].$get({
            param: { slug: this.dealerSlug() },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getModelEnvkvDropdownByModelCodeAndGeneration(modelCode: string, generation: string) {
        const request = await this.api[':slug']['dealer-campaigns'].edit['vehicle-envkv-dropdown'].$get({
            param: { slug: this.dealerSlug() },
            query: { search: modelCode, generation },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async getGenerationEnvkvDropdownByModelCode(modelCode: string) {
        const request = await this.api[':slug']['dealer-campaigns'].edit['generation-envkv-dropdown'].$get({
            param: { slug: this.dealerSlug() },
            query: { search: modelCode },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async isDealerCampaignSlugAvailable(campaignId: number, campaignSlug: string) {
        const request = await this.api[':slug']['dealer-campaigns'].edit['slug-available'][':campaignId'][
            ':campaignSlug'
        ].$get({
            param: { slug: this.dealerSlug(), campaignId: `${campaignId}`, campaignSlug },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async upsertDealerCampaign(campaign: UpsertDealerCampaignPayload) {
        const request = await this.api[':slug']['dealer-campaigns'].edit.upsert.$post({
            param: { slug: this.dealerSlug() },
            json: campaign,
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async deleteDealerCampaign(campaignId: number) {
        const request = await this.api[':slug']['dealer-campaigns'].edit.delete[':campaignId'].$delete({
            param: { slug: this.dealerSlug(), campaignId: `${campaignId}` },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }

    async saveImageTextContent(identifier: Identifiers, value: ImageTextContent) {
        const request = await this.api[':slug'].edit['image-text-content'].$post({
            param: { slug: this.dealerSlug() },
            json: {
                identifier,
                value,
            },
        });
        if (!request.ok) {
            throw new Error(`HTTP Client ${request.status} ${request.statusText}`);
        }
        return request.json();
    }
}
